import React, { useState } from "react";
import { REPORT, HIGHLIGHT } from "../../../core/theme/reportTheme";

// A dropdown with a tick box per option. Nothing ticked = all.
// Ticking every option also counts as all, so the button says "All ...".
// options: plain strings, or { value, label } when the shown name differs.
// searchable: a search box at the top (long lists, e.g. advertisers).
// shortcuts: [{ label, values }] - one click ticks exactly those values
// (e.g. "All except directories").
// marked + onMark: a second button per option, the yellow highlighter
// (Advertisers: highlight without filtering). marked = the highlighted values.
export default function MultiSelect({ options, selected, onChange, allLabel, noun, searchable, shortcuts = [], wide, marked, onMark }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const items = options.map(o => (typeof o === "string" ? { value: o, label: o } : o));
  const labelOf = value => items.find(i => i.value === value)?.label || value;
  const isAll = selected.length === 0;

  const set = values => onChange(values.length === items.length ? [] : values);
  const toggle = value => set(selected.includes(value)
    ? selected.filter(s => s !== value)
    : items.map(i => i.value).filter(v => v === value || selected.includes(v))); // keep the list order
  const isShortcut = values => values.length === selected.length && values.every(v => selected.includes(v));
  const canMark = Boolean(onMark);
  const isMarked = value => (marked || []).includes(value);
  const toggleMark = value => onMark(isMarked(value)
    ? marked.filter(m => m !== value)
    : items.map(i => i.value).filter(v => v === value || isMarked(v)));

  const q = query.trim().toLowerCase();
  const shown = q ? items.filter(i => i.label.toLowerCase().includes(q)) : items;

  const buttonText = isAll
    ? `${allLabel} (${items.length})`
    : selected.length === 1 ? labelOf(selected[0]) : `${selected.length} ${noun}s`;

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} className={REPORT.multiButton} title={isAll ? undefined : selected.map(labelOf).join(", ")}>
        <span className="truncate">{buttonText}</span>
        {canMark && marked.length > 0 && <span className={HIGHLIGHT.count} title={`${marked.length} highlighted: ${marked.map(labelOf).join(", ")}`}>{marked.length}</span>}
        <span className="text-slate-400 text-[0.625rem] shrink-0">▼</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[999]" onClick={() => { setOpen(false); setQuery(""); }} />
          <div className={wide ? REPORT.multiPanelWide : REPORT.multiPanel}>
            {searchable && (
              <div className={REPORT.multiSearchRow}>
                <input
                  type="search" autoFocus value={query} onChange={e => setQuery(e.target.value)}
                  placeholder={`Search ${noun}s`} className={REPORT.multiSearch}
                />
              </div>
            )}
            {!q && (
              <>
                <div className={REPORT.multiOptionRow}>
                  <label className={REPORT.multiOptionLabel}>
                    <input type="checkbox" checked={isAll} onChange={() => onChange([])} />
                    <span>{allLabel} ({items.length})</span>
                  </label>
                  {canMark && (
                    // Yellow while anything is highlighted; click to clear every highlight.
                    <Highlighter
                      on={marked.length > 0}
                      disabled={marked.length === 0}
                      onClick={() => onMark([])}
                      title={marked.length > 0 ? `Remove all ${marked.length} highlight${marked.length === 1 ? "" : "s"}` : "Nothing highlighted"}
                    />
                  )}
                </div>
                {shortcuts.map(s => (
                  <label key={s.label} className={REPORT.multiOption}>
                    <input type="checkbox" checked={isShortcut(s.values)} onChange={() => set(s.values)} />
                    <span>{s.label}</span>
                  </label>
                ))}
                <div className="border-t border-slate-100 my-1" />
              </>
            )}
            {shown.length === 0 && <p className="px-3 py-1 text-xs text-slate-400">No {noun}s match.</p>}
            {shown.map(item => (
              <div key={item.value} className={`${REPORT.multiOptionRow} ${canMark && isMarked(item.value) ? HIGHLIGHT.row : ""}`}>
                <label className={REPORT.multiOptionLabel}>
                  <input type="checkbox" checked={selected.includes(item.value)} onChange={() => toggle(item.value)} />
                  <span className="truncate" title={item.label}>{item.label}</span>
                </label>
                {canMark && (
                  <Highlighter
                    on={isMarked(item.value)}
                    onClick={() => toggleMark(item.value)}
                    title={isMarked(item.value) ? `Remove the highlight from ${item.label}` : `Highlight ${item.label} in yellow`}
                  />
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// The highlighter pen button: grey when off; yellow, with a yellow stroke under
// the pen, when on.
function Highlighter({ on, disabled, onClick, title }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title} aria-pressed={on}
      className={on ? HIGHLIGHT.markOn : HIGHLIGHT.mark}
    >
      <svg viewBox="0 0 16 16" className="w-4 h-4" aria-hidden="true">
        <path d="M10.2 1.8l4 4-6.4 6.4H3.8V8.2z" fill={on ? "#fde047" : "none"} stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M2 14.6h12" stroke={on ? "#facc15" : "currentColor"} strokeWidth={on ? 2.4 : 1.3} strokeLinecap="round" />
      </svg>
    </button>
  );
}
